import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Phone, Menu, X } from "lucide-react";
import { COMPANY, LOGO_URL } from "@/lib/site";

const LINKS = [
    { to: "/", label: "Home", end: true },
    { to: "/services", label: "Services" },
    { to: "/photos", label: "Photos" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export default function Navbar() {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [location.pathname]);

    return (
        <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled
                    ? "bg-brand-surface/85 backdrop-blur-xl border-b border-white/10"
                    : "bg-transparent border-b border-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                <div className="flex items-center justify-between h-28 md:h-32">
                    <Link
                        to="/"
                        data-testid="nav-logo"
                        className="flex items-center gap-4 group"
                    >
                        <img
                            src={LOGO_URL}
                            alt="LMJ International"
                            className="h-20 md:h-24 w-auto object-contain drop-shadow-[0_4px_18px_rgba(0,85,164,0.45)]"
                        />
                        <span className="hidden lg:inline font-mono text-[10px] tracking-[0.2em] text-brand-blue uppercase border-l border-white/15 pl-4 ml-1 leading-relaxed">
                            NAVSEA · ABS<br />MIL-SPEC
                        </span>
                    </Link>

                    <nav className="hidden lg:flex items-center gap-1">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`nav-link-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `relative px-4 py-2 font-display font-semibold uppercase text-sm tracking-wider transition-colors ${
                                        isActive
                                            ? "text-white"
                                            : "text-slate-400 hover:text-white"
                                    }`
                                }
                            >
                                {({ isActive }) => (
                                    <>
                                        {l.label}
                                        {isActive && (
                                            <span className="absolute left-4 right-4 bottom-1 h-px bg-brand-blue" />
                                        )}
                                    </>
                                )}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="flex items-center gap-3">
                        <a
                            href={`tel:${COMPANY.phoneTel}`}
                            data-testid="nav-phone"
                            className="hidden md:inline-flex items-center gap-2 font-mono text-xs text-slate-300 hover:text-white transition-colors"
                        >
                            <Phone className="h-3.5 w-3.5 text-brand-blue" />
                            {COMPANY.phone}
                        </a>
                        <Link
                            to="/contact"
                            data-testid="nav-cta-quote"
                            className="hidden md:inline-flex items-center gap-2 bg-brand-blue hover:bg-[#004080] text-white font-display font-bold uppercase text-xs tracking-[0.18em] px-5 py-3 transition-colors"
                        >
                            Get a Quote
                            <span className="font-mono">→</span>
                        </Link>
                        <button
                            data-testid="nav-mobile-toggle"
                            onClick={() => setOpen((v) => !v)}
                            className="lg:hidden p-2 text-white"
                            aria-label="Toggle navigation"
                        >
                            {open ? (
                                <X className="h-6 w-6" />
                            ) : (
                                <Menu className="h-6 w-6" />
                            )}
                        </button>
                    </div>
                </div>
            </div>
            {open && (
                <div
                    data-testid="nav-mobile-menu"
                    className="lg:hidden bg-brand-surface/95 backdrop-blur-xl border-t border-white/10"
                >
                    <nav className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-2">
                        {LINKS.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.end}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}`}
                                className={({ isActive }) =>
                                    `py-3 border-b border-white/10 font-display font-semibold uppercase tracking-wider ${
                                        isActive
                                            ? "text-brand-blue"
                                            : "text-white"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/contact"
                            data-testid="nav-mobile-cta"
                            className="mt-4 inline-flex items-center justify-center bg-brand-blue text-white font-display font-bold uppercase tracking-[0.18em] py-4"
                        >
                            Get a Quote →
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}
